import { create } from 'zustand'
import { setAutoFreeze } from 'immer'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { datadogLogs } from '@datadog/browser-logs'
import {
  isBirdieProgressRow,
  parsePuttResult,
  parseRoundTypeReveal,
  ROUND_TYPE_EVENT_TYPE,
  type BirdieProgressRowLike,
  type PuttEntries,
  type RevealedRoundType
} from 'src/components/BirdieBoard/livePutts'
import { isDevtoolsEnabled } from './utils/isDevtoolsEnabled'

setAutoFreeze(false)

export interface BirdiePuttsState {
  /** The round the board is showing. Rows for anything else are dropped. */
  currentRaceId?: string
  putts: Record<string, PuttEntries>
  /**
   * The round's drawn type (B36) — the live half of the reveal the settle payload completes. Off the
   * `round_type` row the engine arms on the last putt's cup tell, or the last putt row that repeats
   * it as the fallback. Its own map rather than a field on an entry: it belongs to the ROUND.
   */
  roundTypes: Record<string, RevealedRoundType>
  /** When this client first received the round's type — the hold is measured from here, like a ring's. */
  roundTypeAtMs: Record<string, number>
  /** One `birdie_putt_event_rejected` warn per ROUND, not per bad row — three
   *  putts of a drifted payload would otherwise be three identical warns. */
  rejectedRaceIds: Record<string, true>
}

export interface BirdiePuttsActions {
  puttResult: (row: BirdieProgressRowLike, receivedAtMs?: number) => void
  roundTypeRevealed: (row: BirdieProgressRowLike, receivedAtMs?: number) => void
  setCurrentRace: (raceId: string) => void
  clearRace: (raceId: string) => void
}

type BirdiePuttsStore = BirdiePuttsState & { actions: BirdiePuttsActions }

/**
 * The Birdie board's LIVE per-putt score — deliberately its own slice, never
 * `raceEvents`.
 *
 * The engine writes one `race_event` row per putt (`putt_result`), ~2 s after
 * the frame left the encoder. Those rows reach the widget on the same shared
 * broadcast every other race event uses, but they are a Birdie-only signal:
 * `getLatestEvent` has no type filter, so letting them into `raceEvents` would
 * make the shared countdown, status pill, banner and toolbar treat "putt 2
 * missed" as the round's latest lifecycle event. Both entry points (the socket
 * handler and the hydration mapper) divert them here instead.
 *
 * See `docs/superpowers/specs/2026-09-07-birdie-live-putt-events-design.md`.
 */
export const useBirdiePuttsStore = create<BirdiePuttsStore>()(
  devtools(
    immer((set, get) => ({
      currentRaceId: undefined,
      putts: {},
      roundTypes: {},
      roundTypeAtMs: {},
      rejectedRaceIds: {},
      actions: {
        puttResult: (row: BirdieProgressRowLike, receivedAtMs?: number) => {
          const raceId = row.race_id
          const state = get()
          // Before the board claims a round (hydration can beat the mount) any
          // race is accepted and `setCurrentRace` prunes the rest.
          if (state.currentRaceId !== undefined && state.currentRaceId !== raceId) return

          const parsed = parsePuttResult(row)
          if (parsed === undefined) {
            if (state.rejectedRaceIds[raceId] === true) return
            set(
              (draft) => {
                draft.rejectedRaceIds[raceId] = true
              },
              undefined,
              'puttResultRejected'
            )
            // NEVER log the payload: it carries the result, and a spoiler in
            // the log is a spoiler one copy-paste away from the UI.
            datadogLogs.logger.warn('birdie_putt_event_rejected', {
              user_event: { raceId, eventType: row.event_type }
            })
            return
          }

          // Idempotent per (raceId, putt): the FIRST arrival's stamp is the one
          // the hold is measured from, so a duplicate must not push it forward.
          if (state.putts[raceId]?.[parsed.putt] !== undefined) return
          const stamp = receivedAtMs ?? Date.now()
          set(
            (draft) => {
              const entries: PuttEntries = draft.putts[raceId] ?? {}
              entries[parsed.putt] = { result: parsed.result, receivedAtMs: stamp }
              draft.putts[raceId] = entries
              // The fallback reveal: the last putt row repeats the type for a board that missed the
              // `round_type` row. First arrival wins, so a reveal already held keeps its own stamp.
              if (parsed.roundType !== undefined && draft.roundTypes[raceId] === undefined) {
                draft.roundTypes[raceId] = parsed.roundType
                draft.roundTypeAtMs[raceId] = stamp
              }
            },
            undefined,
            'puttResult'
          )
        },

        roundTypeRevealed: (row: BirdieProgressRowLike, receivedAtMs?: number) => {
          const raceId = row.race_id
          const state = get()
          if (state.currentRaceId !== undefined && state.currentRaceId !== raceId) return
          const roundType = parseRoundTypeReveal(row)
          if (roundType === undefined) {
            if (state.rejectedRaceIds[raceId] === true) return
            set(
              (draft) => {
                draft.rejectedRaceIds[raceId] = true
              },
              undefined,
              'roundTypeRejected'
            )
            // NEVER log the payload: it is the reveal.
            datadogLogs.logger.warn('birdie_putt_event_rejected', {
              user_event: { raceId, eventType: row.event_type }
            })
            return
          }
          // Idempotent per round: the FIRST arrival's stamp is the one the hold is measured from.
          if (state.roundTypes[raceId] !== undefined) return
          const stamp = receivedAtMs ?? Date.now()
          set(
            (draft) => {
              draft.roundTypes[raceId] = roundType
              draft.roundTypeAtMs[raceId] = stamp
            },
            undefined,
            'roundTypeRevealed'
          )
        },

        setCurrentRace: (raceId: string) => {
          if (get().currentRaceId === raceId) return
          set(
            (draft) => {
              draft.currentRaceId = raceId
              const kept = draft.putts[raceId]
              draft.putts = kept === undefined ? {} : { [raceId]: kept }
              const keptType = draft.roundTypes[raceId]
              draft.roundTypes = keptType === undefined ? {} : { [raceId]: keptType }
              const keptAt = draft.roundTypeAtMs[raceId]
              draft.roundTypeAtMs = keptAt === undefined ? {} : { [raceId]: keptAt }
              draft.rejectedRaceIds = {}
            },
            undefined,
            'setCurrentRace'
          )
        },

        clearRace: (raceId: string) => {
          const state = get()
          if (
            state.putts[raceId] === undefined &&
            state.roundTypes[raceId] === undefined &&
            state.rejectedRaceIds[raceId] === undefined
          ) {
            return
          }
          set(
            (draft) => {
              delete draft.putts[raceId]
              delete draft.roundTypes[raceId]
              delete draft.roundTypeAtMs[raceId]
              delete draft.rejectedRaceIds[raceId]
            },
            undefined,
            'clearRace'
          )
        }
      }
    })),
    { name: 'BirdiePuttsStore', enabled: isDevtoolsEnabled() }
  )
)

/**
 * The single call the two shared files make. Returns `true` when the row is a
 * Birdie progress row and has therefore been CONSUMED — the caller must not
 * pass it on to `raceEvents`. A `putt_result` whose payload cannot be parsed is
 * still consumed: it is unmistakably a Birdie progress row, and letting it fall
 * through on a payload drift is exactly the regression this routing prevents.
 */
export const routeBirdieProgressRow = (
  row: BirdieProgressRowLike,
  receivedAtMs?: number
): boolean => {
  if (!isBirdieProgressRow(row)) return false
  const actions = useBirdiePuttsStore.getState().actions
  if (row.event_type === ROUND_TYPE_EVENT_TYPE) actions.roundTypeRevealed(row, receivedAtMs)
  else actions.puttResult(row, receivedAtMs)
  return true
}

export const useBirdiePuttsActions = (): BirdiePuttsActions =>
  useBirdiePuttsStore((state) => state.actions)

export const useBirdiePutts = (raceId: string | undefined): PuttEntries | undefined =>
  useBirdiePuttsStore((state) => (raceId === undefined ? undefined : state.putts[raceId]))

/** The round's type once a row has revealed it — undefined until one does, or for an engine that
 *  publishes none, which is what leaves both round-type tiles alive. RAW: the host applies the hold
 *  (`roundTypeVisible`) before drawing it. */
export const useBirdieRoundType = (raceId: string | undefined): RevealedRoundType | undefined =>
  useBirdiePuttsStore((state) => (raceId === undefined ? undefined : state.roundTypes[raceId]))

/** When this client first received the round's type; undefined until it has. */
export const useBirdieRoundTypeAtMs = (raceId: string | undefined): number | undefined =>
  useBirdiePuttsStore((state) => (raceId === undefined ? undefined : state.roundTypeAtMs[raceId]))
